import { KatButton, KatModal } from '@amzn/katal-react';
import React, { FC } from 'react';
import styles from './ConfirmationDialog.module.css';

export interface ConfirmationDialogProps {
  /**
   * Whether the dialog is visible
   */
  isVisible: boolean;
  /**
   * Title of the dialog
   */
  title: string;
  /**
   * Message to display in the dialog
   */
  message: string;
  /**
   * Text for the confirm button
   * @default "Confirm"
   */
  confirmText?: string;
  /**
   * Text for the cancel button
   * @default "Cancel"
   */
  cancelText?: string;
  /**
   * Variant for the confirm button
   * @default "primary"
   */
  confirmVariant?: 'primary' | 'secondary' | 'danger';
  /**
   * Callback when confirm is clicked
   */
  onConfirm: () => void;
  /**
   * Callback when cancel is clicked or dialog is closed
   */
  onCancel: () => void;
  /**
   * Additional class name for the dialog
   */
  className?: string;
  /**
   * Test ID for the dialog
   * @default "confirmation-dialog"
   */
  'data-testid'?: string;
}

/**
 * A reusable confirmation dialog component that uses KatModal
 *
 * @example
 * ```tsx
 * <ConfirmationDialog
 *   isVisible={showDialog}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item?"
 *   confirmText="Delete"
 *   confirmVariant="danger"
 *   onConfirm={handleDelete}
 *   onCancel={() => setShowDialog(false)}
 * />
 * ```
 */
export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  isVisible,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  className,
  'data-testid': testId = 'confirmation-dialog',
}) => (
  <KatModal visible={isVisible} onClose={onCancel} className={className} data-testid={testId}>
    <span slot="title">{title}</span>
    <p className={styles.message}>{message}</p>
    <div slot="footer" className={styles.footer}>
      <KatButton
        variant="secondary"
        label={cancelText}
        onClick={onCancel}
        data-testid={`${testId}-cancel`}
      />
      <KatButton
        variant={confirmVariant}
        label={confirmText}
        onClick={onConfirm}
        data-testid={`${testId}-confirm`}
      />
    </div>
  </KatModal>
);
